import React from 'react';
import { Shield, Lock, UserX, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Consent Block Component
 * Implements granular consent system with separate checkboxes
 */
export const ConsentBlock = ({ 
  consents, 
  onConsentChange, 
  errors = {},
  lang = 'en'
}) => {
  const content = {
    en: {
      title: "Your data stays yours",
      subtitle: "We use your data to help you improve your financial life. We never sell your personal data.",
      terms: {
        label: "I agree to the",
        termsLink: "Terms of Service",
        and: "and",
        privacyLink: "Privacy Policy",
        required: "(required)"
      },
      financial: {
        label: "I expressly consent to the use of my financial data (income, expenses, assets, documents) to generate insights",
        required: "(required)"
      },
      ai: {
        label: "I understand that insights are generated using AI and are not financial advice",
        required: "(required)"
      },
      marketing: {
        label: "I agree to receive updates and offers",
        optional: "(optional)"
      }
    },
    es: {
      title: "Tus datos son tuyos",
      subtitle: "Usamos tus datos para ayudarte a mejorar tu vida financiera. Nunca vendemos tus datos personales.",
      terms: {
        label: "Acepto los",
        termsLink: "Términos de Servicio",
        and: "y la",
        privacyLink: "Política de Privacidad",
        required: "(requerido)"
      },
      financial: {
        label: "Doy mi consentimiento expreso para el uso de mis datos financieros (ingresos, gastos, activos, documentos) para generar análisis",
        required: "(requerido)"
      },
      ai: {
        label: "Entiendo que los análisis son generados con IA y no constituyen asesoría financiera",
        required: "(requerido)"
      },
      marketing: {
        label: "Acepto recibir actualizaciones y ofertas",
        optional: "(opcional)"
      }
    }
  };

  const c = content[lang] || content.en;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Shield className="w-5 h-5 text-yellow-500" />
          <h3 className="text-white font-semibold">{c.title}</h3>
        </div>
        <p className="text-gray-400 text-sm">{c.subtitle}</p>
      </div>

      {/* Consent Checkboxes */}
      <div className="space-y-3">
        {/* Terms + Privacy (Required) */}
        <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
          errors.terms ? 'bg-red-500/10 border border-red-500/50' : 'bg-gray-800/50 hover:bg-gray-800'
        }`}>
          <input
            type="checkbox"
            checked={consents.terms || false}
            onChange={(e) => onConsentChange('terms', e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-700 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-gray-900"
          />
          <span className="text-sm text-gray-300">
            {c.terms.label}{' '}
            <Link to="/terms" className="text-yellow-500 hover:text-yellow-400 underline" target="_blank">
              {c.terms.termsLink}
            </Link>{' '}
            {c.terms.and}{' '}
            <Link to="/privacy" className="text-yellow-500 hover:text-yellow-400 underline" target="_blank">
              {c.terms.privacyLink}
            </Link>
            <span className="text-gray-500 ml-1">{c.terms.required}</span>
          </span>
        </label>

        {/* Sensitive Financial Data (Required) */}
        <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
          errors.financial ? 'bg-red-500/10 border border-red-500/50' : 'bg-gray-800/50 hover:bg-gray-800'
        }`}>
          <input
            type="checkbox"
            checked={consents.financial || false}
            onChange={(e) => onConsentChange('financial', e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-700 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-gray-900"
          />
          <span className="text-sm text-gray-300">
            {c.financial.label}
            <span className="text-gray-500 ml-1">{c.financial.required}</span>
          </span>
        </label>

        {/* AI Usage (Required) */}
        <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
          errors.ai ? 'bg-red-500/10 border border-red-500/50' : 'bg-gray-800/50 hover:bg-gray-800'
        }`}>
          <input
            type="checkbox"
            checked={consents.ai || false}
            onChange={(e) => onConsentChange('ai', e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-700 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-gray-900"
          />
          <span className="text-sm text-gray-300">
            {c.ai.label}
            <span className="text-gray-500 ml-1">{c.ai.required}</span>
          </span>
        </label>

        {/* Marketing (Optional) */}
        <label className="flex items-start gap-3 p-3 rounded-lg cursor-pointer bg-gray-800/30 hover:bg-gray-800/50 transition-all">
          <input
            type="checkbox"
            checked={consents.marketing || false}
            onChange={(e) => onConsentChange('marketing', e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-700 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-gray-900"
          />
          <span className="text-sm text-gray-400">
            {c.marketing.label}
            <span className="text-gray-500 ml-1">{c.marketing.optional}</span>
          </span>
        </label>
      </div>

      {/* Error message */}
      {Object.keys(errors).length > 0 && (
        <p className="text-red-400 text-xs text-center">
          {lang === 'es' 
            ? 'Por favor acepta los consentimientos requeridos para continuar'
            : 'Please accept the required consents to continue'
          }
        </p>
      )}
    </div>
  );
};

/**
 * Trust Card Component
 * Visual trust reinforcement with icons and bullet points
 */
export const TrustCard = ({ lang = 'en' }) => {
  const content = {
    en: {
      title: "Your privacy is non-negotiable",
      points: [
        { icon: UserX, text: "We never sell your personal data" },
        { icon: Lock, text: "Your financial information is encrypted" },
        { icon: Shield, text: "You can delete your data anytime" }
      ]
    },
    es: {
      title: "Tu privacidad no es negociable",
      points: [
        { icon: UserX, text: "Nunca vendemos tus datos personales" },
        { icon: Lock, text: "Tu información financiera está encriptada" },
        { icon: Shield, text: "Puedes borrar tus datos en cualquier momento" }
      ]
    }
  };

  const c = content[lang] || content.en;

  return (
    <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-xl p-4 shadow-lg">
      <h4 className="text-white font-medium text-sm mb-3 flex items-center gap-2">
        <Shield className="w-4 h-4 text-emerald-400" />
        {c.title}
      </h4>
      <div className="space-y-2">
        {c.points.map((point, index) => {
          const Icon = point.icon;
          return (
            <div key={index} className="flex items-center gap-2 text-gray-300 text-xs">
              <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Icon className="w-3 h-3 text-emerald-400" />
              </div>
              <span>{point.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Data Input Reassurance Component
 * Micro reassurance message for sensitive data input moments
 */
export const DataReassurance = ({ type = 'default', lang = 'en' }) => {
  const messages = {
    en: {
      default: "Only you can see this data. We use it to generate your insights.",
      expense: "Your expenses are private and encrypted.",
      income: "Your income data is secure and never shared.",
      financial: "This information helps us personalize your experience."
    },
    es: {
      default: "Solo tú puedes ver estos datos. Los usamos para generar tus análisis.",
      expense: "Tus gastos son privados y están encriptados.",
      income: "Tus datos de ingresos son seguros y nunca se comparten.",
      financial: "Esta información nos ayuda a personalizar tu experiencia."
    }
  };

  const c = messages[lang] || messages.en;
  const message = c[type] || c.default;

  return (
    <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-800/30 rounded-lg px-3 py-2">
      <Lock className="w-3 h-3 text-emerald-500 flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
};

/**
 * Footer Trust Signal Component
 * Persistent trust signal for dashboard
 */
export const TrustSignal = ({ lang = 'en', onClick }) => {
  const text = lang === 'es' 
    ? 'Tus datos son privados y seguros' 
    : 'Your data is private and secure';

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-400 transition-colors"
    >
      <Lock className="w-3 h-3 text-emerald-500" />
      <span>{text}</span>
    </button>
  );
};

/**
 * Landing Page Trust Text Component
 * Subtle trust reinforcement near CTA
 */
export const LandingTrustText = ({ lang = 'en' }) => {
  const text = lang === 'es'
    ? 'Seguro. Privado. Tus datos nunca se venden.'
    : 'Secure. Private. Your data is never sold.';

  return (
    <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
      <Lock className="w-3 h-3 text-emerald-500" />
      <span>{text}</span>
    </div>
  );
};

/**
 * Validate required consents
 */
export const validateConsents = (consents) => {
  const errors = {};
  if (!consents.terms) errors.terms = true;
  if (!consents.financial) errors.financial = true;
  if (!consents.ai) errors.ai = true;
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Track consent analytics
 */
export const trackConsentAnalytics = (event, data = {}) => {
  console.log('[Analytics] Consent Event:', { event, ...data, timestamp: new Date().toISOString() });
  
  // Store for analytics
  const events = JSON.parse(sessionStorage.getItem('consentAnalytics') || '[]');
  events.push({ event, data, timestamp: new Date().toISOString() });
  sessionStorage.setItem('consentAnalytics', JSON.stringify(events));
};
